/**
 * Cloudflare Turnstile integration
 *
 * Loads the Turnstile script on demand and produces challenge tokens for the
 * widget session bootstrap. Uses execute-on-demand with interaction-only
 * appearance, so nothing is visible unless Cloudflare decides the visitor
 * needs an interactive challenge.
 */

const TURNSTILE_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
const TOKEN_TIMEOUT_MS = 30000;
// When Cloudflare escalates to a visible interactive challenge, the visitor
// needs human time to react — the short automatic timeout must not kill the
// pending request under their cursor (observed as "solved it, then 600010").
const INTERACTIVE_TOKEN_TIMEOUT_MS = 150000;

let scriptPromise = null;

function loadTurnstileScript() {
  if (window.turnstile) {
    return Promise.resolve(window.turnstile);
  }
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = TURNSTILE_SRC;
      script.async = true;
      script.onload = () => resolve(window.turnstile);
      script.onerror = () => {
        scriptPromise = null;
        reject(new Error('Failed to load Turnstile'));
      };
      document.head.appendChild(script);
    });
  }
  return scriptPromise;
}

const MAX_CHALLENGE_RETRIES = 2;
const RETRY_BASE_DELAY_MS = 400;
// Reset is internally asynchronous; calling execute() in the same tick is a
// known intermittent-failure source (600xxx). A short gap avoids the race.
const RESET_EXECUTE_GAP_MS = 60;

// Cloudflare documents 3xxxxx (crashed challenge) and 6xxxxx (client
// execution error, e.g. the intermittent 600010) as retryable.
function isRetryableTurnstileError(code) {
  const text = String(code || '');
  return text.startsWith('3') || text.startsWith('6');
}

export function createTurnstileProvider(siteKey) {
  let widgetId = null;
  let container = null;
  let pending = null;
  let retriesLeft = 0;

  function settle(fn, value) {
    if (pending) {
      const current = pending;
      pending = null;
      current[fn](value);
    }
    if (container) {
      container.style.display = 'none';
    }
  }

  function runChallenge(turnstile) {
    // Tokens are single-use: reset before each execute for a fresh one, with
    // a short gap so the async reset settles before execute (600xxx race).
    turnstile.reset(widgetId);
    setTimeout(() => {
      if (pending) turnstile.execute(widgetId);
    }, RESET_EXECUTE_GAP_MS);
  }

  function ensureWidget(turnstile) {
    if (widgetId !== null) return;

    // Turnstile renders into the light DOM; keep the container out of the way
    // and only show it when an interactive challenge is required.
    container = document.createElement('div');
    container.className = 'ragsense-turnstile';
    container.style.position = 'fixed';
    container.style.bottom = '96px';
    container.style.right = '20px';
    container.style.zIndex = '2147483000';
    container.style.display = 'none';
    document.body.appendChild(container);

    widgetId = turnstile.render(container, {
      sitekey: siteKey,
      execution: 'execute',
      appearance: 'interaction-only',
      'before-interactive-callback': () => {
        container.style.display = 'block';
        if (pending && pending.onInteractive) pending.onInteractive();
      },
      callback: (token) => settle('resolve', token),
      'error-callback': (code) => {
        if (pending && retriesLeft > 0 && isRetryableTurnstileError(code)) {
          retriesLeft -= 1;
          const attempt = MAX_CHALLENGE_RETRIES - retriesLeft;
          console.warn(`RAGSense widget: Turnstile error ${code || 'unknown'} — retrying (${attempt}/${MAX_CHALLENGE_RETRIES})`);
          setTimeout(() => {
            if (pending) runChallenge(window.turnstile);
          }, RETRY_BASE_DELAY_MS * attempt);
          return true; // handled — suppress Turnstile's own failure handling
        }
        // 110200 means this page's hostname is missing from the Turnstile
        // widget's domain allowlist in the Cloudflare dashboard.
        console.warn(`RAGSense widget: Turnstile error ${code || 'unknown'} on ${window.location.hostname}`);
        settle('reject', new Error(`Turnstile challenge failed (${code || 'unknown'})`));
        return true;
      },
      'timeout-callback': () => settle('reject', new Error('Turnstile challenge timed out'))
    });
  }

  return {
    async getToken() {
      const turnstile = await loadTurnstileScript();
      ensureWidget(turnstile);

      if (pending) {
        pending.reject(new Error('Turnstile challenge superseded'));
        pending = null;
      }

      retriesLeft = MAX_CHALLENGE_RETRIES;

      return new Promise((resolve, reject) => {
        let timer = setTimeout(() => {
          settle('reject', new Error('Turnstile challenge timed out'));
        }, TOKEN_TIMEOUT_MS);

        pending = {
          resolve: (token) => { clearTimeout(timer); resolve(token); },
          reject: (error) => { clearTimeout(timer); reject(error); },
          onInteractive: () => {
            clearTimeout(timer);
            timer = setTimeout(() => {
              settle('reject', new Error('Turnstile challenge timed out'));
            }, INTERACTIVE_TOKEN_TIMEOUT_MS);
          }
        };

        runChallenge(turnstile);
      });
    }
  };
}
